import { useEffect, useState } from 'react';
import { Award, HeartHandshake, Globe, Facebook, Instagram } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const SECTION_CONFIG = {
  Sponsors: {
    title: 'Sponsors',
    subtitle: 'The wonderful organisations making Paws in the Park 2026 possible',
    icon: Award,
    iconBg: 'bg-gradient-to-br from-amber-100 to-yellow-100',
    iconColor: 'text-amber-700',
    cardBorder: 'hover:border-amber-300',
    badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',
    buttonClass: 'border-amber-200 text-amber-700 hover:bg-amber-50',
    label: 'Sponsor',
  },
  Supporters: {
    title: 'Supporters',
    subtitle: 'Community friends and contributors who help bring Paws in the Park to life',
    icon: HeartHandshake,
    iconBg: 'bg-gradient-to-br from-emerald-100 to-teal-100',
    iconColor: 'text-emerald-700',
    cardBorder: 'hover:border-emerald-300',
    badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    buttonClass: 'border-emerald-200 text-emerald-700 hover:bg-emerald-50',
    label: 'Supporter',
  },
};

const SECTION_ORDER = ['Sponsors', 'Supporters'];

const PartnerCard = ({ item, config }) => {
  const Icon = config.icon;
  const hasImage = Boolean(item.image_url);
  return (
    <Card
      className={`border-slate-200 ${item.coming_soon ? 'opacity-75' : config.cardBorder} hover:shadow-lg transition-all duration-300 overflow-hidden`}
      data-testid={`partner-card-${item.id}`}
    >
      <div className={hasImage ? 'md:flex md:items-stretch' : ''}>
        {hasImage && (
          <div className="md:w-1/3 lg:w-2/5 md:flex-shrink-0 bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center">
            <img
              src={item.image_url}
              alt={`${item.name} — Paws in the Park ${config.label.toLowerCase()}`}
              className="w-full h-48 md:h-full max-h-72 md:max-h-none object-contain p-4"
              data-testid={`partner-image-${item.id}`}
              loading="lazy"
            />
          </div>
        )}
        <div className={hasImage ? 'md:flex-1' : ''}>
          <CardHeader>
            <div className="flex items-start justify-between mb-3">
              <div className={`${config.iconBg} w-14 h-14 rounded-lg flex items-center justify-center`}>
                <Icon className={`h-7 w-7 ${config.iconColor}`} />
              </div>
              <Badge variant="outline" className={item.coming_soon ? 'bg-amber-50 text-amber-700 border-amber-200' : config.badgeClass}>
                {item.coming_soon ? 'Coming Soon' : config.label}
              </Badge>
            </div>
            <CardTitle className="text-2xl mb-1">{item.name}</CardTitle>
            {item.subtitle && (
              <p className="text-slate-500 text-sm font-medium">— {item.subtitle}</p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {item.description && (
              <CardDescription className="text-slate-600 text-base leading-relaxed whitespace-pre-line">
                {item.description}
              </CardDescription>
            )}

            {!item.coming_soon && (item.website || item.facebook || item.instagram) && (
              <div className="flex flex-wrap gap-3 pt-2">
                {item.website && (
                  <Button asChild variant="outline" size="sm" className={config.buttonClass}>
                    <a href={item.website} target="_blank" rel="noopener noreferrer">
                      <Globe className="h-4 w-4 mr-2" />
                      Website
                    </a>
                  </Button>
                )}
                {item.facebook && (
                  <Button asChild variant="outline" size="sm" className="border-blue-200 text-blue-700 hover:bg-blue-50">
                    <a href={item.facebook} target="_blank" rel="noopener noreferrer">
                      <Facebook className="h-4 w-4 mr-2" />
                      Facebook
                    </a>
                  </Button>
                )}
                {item.instagram && (
                  <Button asChild variant="outline" size="sm" className="border-pink-200 text-pink-700 hover:bg-pink-50">
                    <a href={item.instagram} target="_blank" rel="noopener noreferrer">
                      <Instagram className="h-4 w-4 mr-2" />
                      Instagram
                    </a>
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </div>
      </div>
    </Card>
  );
};

const SupporterCard = ({ item, config }) => {
  const Icon = config.icon;
  const hasImage = Boolean(item.image_url);
  const primaryLink = item.website || item.facebook || item.instagram;

  const cardBody = (
    <div className="p-4 flex flex-col items-center text-center h-full">
      {hasImage ? (
        <div className="w-full h-24 flex items-center justify-center mb-3 bg-gradient-to-br from-slate-50 to-white rounded">
          <img
            src={item.image_url}
            alt={`${item.name} — Paws in the Park supporter`}
            className="max-h-full max-w-full object-contain"
            data-testid={`supporter-image-${item.id}`}
            loading="lazy"
          />
        </div>
      ) : (
        <div className={`${config.iconBg} w-12 h-12 rounded-full flex items-center justify-center mb-3`}>
          <Icon className={`h-6 w-6 ${config.iconColor}`} />
        </div>
      )}
      <p className="font-semibold text-slate-900 text-sm leading-snug">{item.name}</p>
      {item.subtitle && (
        <p className="text-slate-500 text-xs mt-1 line-clamp-2">{item.subtitle}</p>
      )}
    </div>
  );

  return (
    <Card
      className={`border-slate-200 ${item.coming_soon ? 'opacity-75' : config.cardBorder} hover:shadow-md transition-all duration-300 overflow-hidden`}
      data-testid={`supporter-card-${item.id}`}
    >
      {primaryLink && !item.coming_soon ? (
        <a href={primaryLink} target="_blank" rel="noopener noreferrer" className="block h-full">
          {cardBody}
        </a>
      ) : (
        cardBody
      )}
    </Card>
  );
};

const SponsorsSupporters = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/vendors`)
      .then((res) => res.json())
      .then((data) => {
        setVendors(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const grouped = SECTION_ORDER.map((section) => {
    let items;
    if (section === 'Sponsors') {
      items = vendors.filter((v) => v.section === 'Sponsors');
    } else {
      // Supporters section = anyone tagged with section 'Supporters' OR any vendor flagged also_supporter
      items = vendors.filter((v) => v.section === 'Supporters' || v.also_supporter === true);
    }
    return { section, config: SECTION_CONFIG[section], items };
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="w-full h-full bg-gradient-to-br from-amber-50 via-white to-emerald-50" />
        </div>

        <div className="relative container mx-auto px-4 py-16 md:py-20">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4 bg-amber-100 text-amber-800 hover:bg-amber-200 border-amber-200">
              Community Partners
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
              Sponsors and Supporters
            </h1>
            <p className="text-lg text-slate-600">
              We are deeply grateful to the sponsors and supporters whose generosity makes
              Paws in the Park 2026 possible. Thank you for standing beside our community.
            </p>
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto space-y-16">
            {loading ? (
              <p className="text-center text-slate-500" data-testid="sponsors-supporters-loading">Loading…</p>
            ) : (
              grouped.map(({ section, config, items }) => (
                <div key={section} data-testid={`section-${section.toLowerCase()}`}>
                  <div className="text-center mb-10">
                    <div className={`${config.iconBg} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4`}>
                      <config.icon className={`h-8 w-8 ${config.iconColor}`} />
                    </div>
                    <h2 className="text-3xl font-bold text-slate-900 mb-3">{config.title}</h2>
                    <p className="text-slate-600 max-w-2xl mx-auto">{config.subtitle}</p>
                  </div>
                  {items.length === 0 ? (
                    <p className="text-center text-slate-500" data-testid={`empty-${section.toLowerCase()}`}>
                      {section === 'Supporters'
                        ? 'Supporters will be announced soon — check back shortly!'
                        : 'Sponsors will be announced soon — check back shortly!'}
                    </p>
                  ) : section === 'Supporters' ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
                      {items.map((item) => (
                        <SupporterCard key={item.id} item={item} config={config} />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {items.map((item) => (
                        <PartnerCard key={item.id} item={item} config={config} />
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default SponsorsSupporters;
